import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtTotals, fmtMoney, STATUS_META, formatAxiosError } from "../lib/api";
import { GROUP_STATUS_META, ungroupableReason, defaultGroupTitle, sumTotals, faultsOf } from "../lib/partnerGroups";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import GroupFaults from "../components/GroupFaults";
import { Plus, Search, Copy, ChevronRight, Trash2, Layers, FolderOpen } from "lucide-react";

/** « à signer / disponible le <date> / signé / en échec » — dossier.sepa (déjà booléen serveur). */
function sepaLabel(sepa) {
  if (sepa.done) return "signé";
  if (sepa.failed) return "en échec";
  if (sepa.available_at && new Date(sepa.available_at) > new Date()) return `disponible le ${new Date(sepa.available_at).toLocaleDateString("fr-FR")}`;
  return "à signer";
}

function DossierBadge({ dossier, id }) {
  if (!dossier) return null;
  const hasDocs = dossier.documents.required.length > 0;
  const hasSepa = dossier.sepa.required;
  const hasPhone = !!dossier.phone?.required;
  if (!hasDocs && !hasSepa && !hasPhone) return null;
  if (dossier.complete) {
    return <span data-testid={`partner-dossier-badge-${id}`} className="text-[11px] px-1.5 py-0.5 rounded-sm font-semibold border bg-emerald-50 text-emerald-700 border-emerald-200">Dossier complet</span>;
  }
  const present = dossier.documents.required.length - dossier.documents.missing.length;
  const parts = [
    hasDocs && `Pièces ${present}/${dossier.documents.required.length}`,
    hasSepa && `Mandat : ${sepaLabel(dossier.sepa)}`,
    hasPhone && !dossier.phone.done && "RIO manquant",
  ].filter(Boolean);
  if (!parts.length) return null; // ex. RIO reçu, offre pas encore signée : rien à signaler
  return (
    <span data-testid={`partner-dossier-badge-${id}`} className="text-[11px] px-1.5 py-0.5 rounded-sm font-semibold border bg-amber-50 text-amber-700 border-amber-200">
      {parts.join(" · ")}
    </span>
  );
}

const plural = (n, one, many) => `${n} ${n > 1 ? many : one}`;

export default function PartnerProposals() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  // Regroupement : mode sélection (identifiants dans l'ordre des clics) puis fenêtre de création.
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [creating, setCreating] = useState(false);
  const [faults, setFaults] = useState([]);

  useEffect(() => { api.get("/partner/proposals").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  // Les dossiers sont un plus : une erreur ici ne doit jamais vider la liste des propositions.
  const loadGroups = () => api.get("/partner/proposal-groups").then((r) => setGroups(Array.isArray(r.data) ? r.data : [])).catch(() => setGroups([]));
  useEffect(() => { loadGroups(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const s = q.trim().toLowerCase();
  const filtered = (items || []).filter((p) => !s || p.title?.toLowerCase().includes(s) || (p.client_name || "").toLowerCase().includes(s));
  const filteredGroups = groups.filter((g) => !s || g.title?.toLowerCase().includes(s) || (g.client_name || "").toLowerCase().includes(s));
  const groupById = Object.fromEntries(groups.map((g) => [g.id, g]));

  const copyLink = (url) => { navigator.clipboard.writeText(url); toast.success("Lien copié"); };

  // Comme le détail : jamais une signée (bouton masqué, 409 côté serveur) ; pièces effacées avec la proposition.
  const remove = async (p) => {
    const linkNote = p.status === "brouillon" ? "" : " Le lien envoyé au client ne fonctionnera plus.";
    const g = p.group_id ? groupById[p.group_id] : null;
    const groupNote = !p.group_id ? "" : !g ? " Elle quittera son dossier groupé."
      : g.member_count === 1 ? ` Elle quittera le dossier « ${g.title} », qui sera supprimé avec elle.` : ` Elle quittera le dossier « ${g.title} ».`;
    if (!window.confirm(`Supprimer définitivement cette proposition ?${linkNote}${groupNote}`)) return;
    setDeletingId(p.id);
    try {
      await api.delete(`/partner/proposals/${p.id}`);
      setItems((list) => list.filter((x) => x.id !== p.id));
      // Le serveur recalcule le dossier (compteur, montants) et le supprime avec sa dernière villa : on le relit.
      if (p.group_id) loadGroups();
      toast.success("Proposition supprimée");
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setDeletingId(null); }
  };

  // « Regrouper » n'a de sens que s'il existe deux brouillons libres d'un même client.
  const freeByClient = {};
  for (const p of items || []) if (!ungroupableReason(p, null)) freeByClient[p.client_id] = (freeByClient[p.client_id] || 0) + 1;
  const canGroup = Object.values(freeByClient).some((n) => n >= 2);

  const selectedRows = (items || []).filter((p) => selected.includes(p.id));
  const anchor = items?.find((p) => p.id === selected[0]) || null; // la première cochée fixe le client du dossier
  const totals = sumTotals(selectedRows);

  const stopSelecting = () => { setSelecting(false); setSelected([]); };
  // Décocher la première déplace l'ancre (la suivante) ; une sélection vide libère tous les clients.
  const toggle = (p) => {
    if (ungroupableReason(p, anchor?.client_id ?? null)) return;
    setSelected((cur) => (cur.includes(p.id) ? cur.filter((x) => x !== p.id) : [...cur, p.id]));
  };

  const openDialog = () => { setTitle(defaultGroupTitle(selectedRows[0]?.client_name)); setFaults([]); setDialogOpen(true); };
  const submit = async () => {
    setCreating(true);
    setFaults([]);
    try {
      const { data } = await api.post("/partner/proposal-groups", { title: title.trim(), proposal_ids: selected });
      toast.success("Dossier créé");
      navigate(`/partner/groups/${data.id}`);
    } catch (e) {
      const listed = faultsOf(e);
      if (listed.length) setFaults(listed); else toast.error(formatAxiosError(e));
    } finally { setCreating(false); }
  };

  return (
    <div className={`p-4 sm:p-6 lg:p-12 max-w-5xl ${selected.length ? "pb-44 lg:pb-28" : ""}`} data-testid="partner-proposals-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-2">Portail partenaire</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Mes propositions</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {selecting ? (
            <Button variant="outline" onClick={stopSelecting} data-testid="partner-group-cancel-selection" className="rounded-sm min-h-[44px] sm:min-h-9">Annuler la sélection</Button>
          ) : canGroup && (
            <Button variant="outline" onClick={() => setSelecting(true)} data-testid="partner-group-start" className="rounded-sm min-h-[44px] sm:min-h-9">
              <Layers className="w-4 h-4 mr-1.5" /> Regrouper
            </Button>
          )}
          <Button onClick={() => navigate("/partner/new")} data-testid="partner-new-proposal-button" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold min-h-[44px] sm:min-h-9">
            <Plus className="w-4 h-4 mr-1.5" /> Nouvelle proposition
          </Button>
        </div>
      </div>

      {selecting && (
        <div className="bg-muted/40 border border-border rounded-sm px-4 py-3 mb-6 text-sm" data-testid="partner-group-hint">
          Choisissez les propositions en brouillon d'un même client.
          {anchor && <> Client : <span className="font-medium">{anchor.client_name}</span>. Celles d'un autre client sont grisées.</>}
        </div>
      )}

      {!selecting && filteredGroups.length > 0 && (
        <section className="mb-8" data-testid="partner-groups-section">
          <h2 className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-3">Dossiers groupés</h2>
          <div className="space-y-3">
            {filteredGroups.map((g) => {
              const meta = GROUP_STATUS_META[g.status] || GROUP_STATUS_META.brouillon;
              return (
                <div key={g.id} className="bg-card border border-border rounded-sm p-4 sm:p-5" data-testid={`partner-group-card-${g.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <button onClick={() => navigate(`/partner/groups/${g.id}`)} data-testid={`partner-group-open-${g.id}`} className="min-w-0 text-left flex-1">
                      <p className="font-medium flex items-center gap-2 flex-wrap">
                        <FolderOpen className="w-4 h-4 text-primary shrink-0" aria-hidden="true" />
                        <span className="break-words min-w-0">{g.title}</span>
                        <span className={`text-[11px] px-2 py-0.5 rounded-sm border font-medium ${meta.color}`}>{meta.label}</span>
                      </p>
                      <p className="text-sm text-muted-foreground mt-0.5">{g.client_name || "Sans client"} · {plural(g.member_count, "villa", "villas")}</p>
                    </button>
                    <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0 mt-1" aria-hidden="true" />
                  </div>
                  <p className="font-mono text-sm font-medium mt-3">{fmtTotals(g.total_one_shot, g.total_recurring, g.currency || "EUR")}</p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <div className="relative max-w-sm mb-6">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" data-testid="partner-proposal-search" className="pl-9 rounded-sm" />
      </div>

      {items === null ? (
        <p className="text-muted-foreground">Chargement…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-card border border-border rounded-sm px-6 py-16 text-center text-muted-foreground" data-testid="empty-partner-proposals">
          Aucune proposition. Créez votre première proposition.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((p) => {
            const meta = STATUS_META[p.status] || STATUS_META.brouillon;
            const reason = selecting ? ungroupableReason(p, anchor?.client_id ?? null) : null;
            const checked = selected.includes(p.id);
            const grouped = p.group_id ? groupById[p.group_id] : null;
            return (
              <div key={p.id} className={`bg-card border rounded-sm p-4 sm:p-5 ${checked ? "border-primary" : "border-border"} ${selecting && reason ? "bg-muted/40" : ""}`} data-testid={`partner-proposal-card-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  {selecting && (
                    <label className="flex items-center justify-center min-w-[44px] min-h-[44px] -ml-3 -mt-3 cursor-pointer shrink-0">
                      <input type="checkbox" checked={checked} disabled={!!reason} onChange={() => toggle(p)} aria-label={`Choisir ${p.title}`}
                        className="w-5 h-5 accent-[hsl(var(--primary))] disabled:cursor-not-allowed" />
                    </label>
                  )}
                  {/* Ligne bloquée : déclarée inactive et hors tabulation (la case voisine porte déjà l'état) ; pas d'atténuation, le motif doit rester lisible. */}
                  <button onClick={() => (selecting ? toggle(p) : navigate(`/partner/proposals/${p.id}`))} className="min-w-0 text-left flex-1"
                    aria-disabled={selecting && reason ? true : undefined} tabIndex={selecting && reason ? -1 : undefined}>
                    <p className="font-medium flex items-center gap-2 flex-wrap">
                      {p.title}
                      <span className={`text-[11px] px-2 py-0.5 rounded-sm border font-medium ${meta.color}`}>{meta.label}</span>
                      {selecting && reason && (
                        <span data-testid={`partner-group-reason-${p.id}`} className="text-[11px] px-2 py-0.5 rounded-sm border font-medium bg-amber-50 text-amber-800 border-amber-200">{reason}</span>
                      )}
                    </p>
                    <p className="text-sm text-muted-foreground mt-0.5">{p.client_name || "Sans client"}</p>
                  </button>
                  {!selecting && (
                    <button onClick={() => navigate(`/partner/proposals/${p.id}`)} data-testid={`partner-proposal-open-${p.id}`} aria-label={`Ouvrir ${p.title}`} className="p-1.5 text-muted-foreground hover:text-primary transition-colors shrink-0">
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <span className="font-mono text-sm font-medium">{fmtTotals(p.total_one_shot, p.total_recurring, p.currency)}</span>
                  <DossierBadge dossier={p.dossier} id={p.id} />
                  {p.group_id && (
                    // Simple repère : on ouvre le dossier depuis la section « Dossiers groupés » ou depuis la fiche de la villa (grandes cibles).
                    <span data-testid={`partner-proposal-group-${p.id}`} className="text-[11px] px-1.5 py-0.5 rounded-sm font-semibold border bg-blue-50 text-blue-700 border-blue-200 break-words min-w-0">
                      {grouped ? `Dans le dossier « ${grouped.title} »` : "Dans un dossier groupé"}
                    </span>
                  )}
                  {p.partner_pricing && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded-sm font-semibold border bg-primary/10 text-primary border-primary/20" data-testid={`partner-margin-${p.id}`}>
                      Marge : {fmtMoney(p.partner_pricing.recurring)}/mois + {fmtMoney(p.partner_pricing.one_shot)}
                    </span>
                  )}
                </div>

                {!selecting && (
                  <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-border">
                    {/* Une villa groupée part avec son dossier : son lien individuel ne se copie pas. */}
                    {!p.group_id && (
                      <button onClick={() => copyLink(p.public_url)} data-testid={`partner-copy-link-${p.id}`}
                        className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-primary transition-colors">
                        <Copy className="w-3.5 h-3.5" /> Copier le lien
                      </button>
                    )}
                    <button onClick={() => navigate(`/partner/proposals/${p.id}`)} data-testid={`partner-detail-link-${p.id}`}
                      className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-primary transition-colors">
                      Ouvrir le détail
                    </button>
                    {p.status !== "acceptée" && (
                      <button onClick={() => remove(p)} disabled={deletingId === p.id} data-testid={`partner-proposal-delete-${p.id}`}
                        className="ml-auto flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50">
                        <Trash2 className="w-3.5 h-3.5" /> Supprimer
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {selecting && selected.length > 0 && (
        // Au-dessus de la barre de navigation mobile (4 rem + zone sûre), contre le bord du contenu sur ordinateur.
        // (65 px = h-16 + 1 px de bordure de la barre de navigation.)
        <div data-testid="partner-group-bar"
          className="fixed inset-x-0 lg:left-64 bottom-[calc(65px+env(safe-area-inset-bottom,0px))] lg:bottom-0 z-30 bg-foreground text-background px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0" role="status">
            <p className="font-semibold">{plural(selected.length, "proposition sélectionnée", "propositions sélectionnées")}</p>
            <p className="text-sm opacity-80 font-mono">{fmtTotals(totals.oneShot, totals.recurring, selectedRows[0]?.currency)}</p>
          </div>
          <Button onClick={openDialog} data-testid="partner-group-create" className="rounded-sm bg-background text-foreground hover:bg-background/90 font-semibold min-h-[44px] flex-1 sm:flex-none">
            Créer le dossier groupé
          </Button>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!creating) setDialogOpen(o); }}>
        <DialogContent className="rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Créer le dossier groupé</DialogTitle>
            <DialogDescription>
              {plural(selectedRows.length, "proposition", "propositions")} de {selectedRows[0]?.client_name || "ce client"}. Le dossier est créé en brouillon : vous pourrez y ajouter ou en retirer des villas.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <label htmlFor="partner-group-title" className="text-sm font-medium">Nom du dossier</label>
            <Input id="partner-group-title" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} data-testid="partner-group-title" className="rounded-sm min-h-[44px]" />
          </div>
          <GroupFaults faults={faults} />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={creating} className="rounded-sm min-h-[44px]">Annuler</Button>
            <Button onClick={submit} disabled={creating || !title.trim()} data-testid="partner-group-submit" className="rounded-sm min-h-[44px] bg-primary hover:bg-primary/90 font-semibold">
              {creating ? "Création…" : "Créer le dossier en brouillon"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
